import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import API from '@/api';
import { showErrorToast } from '@/components/Toast';
import { ErrorIds } from '@/constants/ApiResponse';
import useLogout from '@/hooks/useLogout/useLogout';
import useModal from '@/hooks/useModal/useModal';
import { userTerminateAccountDetailsQueryOptions } from '@/pages/UserProfile/queries/userTerminateAccountDetailsQuery/userTerminateAccountDetailsQuery';
import { selectTotalOutstandingDebt } from '@/pages/UserProfile/queries/userTerminateAccountDetailsQuery/userTerminateAccountDetailsQuerySelectors';
import { parseApiError } from '@/utils/error';
import { assertCondition } from '@/utils/typescript';

const useTerminateAccountMutation = () => {
  const { t } = useTranslation('ErrorToast');
  const { openModal, closeModal } = useModal();
  const logout = useLogout();

  const { data: totalOutstandingDebt } = useQuery({
    ...userTerminateAccountDetailsQueryOptions(),
    select: selectTotalOutstandingDebt,
  });

  return useMutation({
    mutationFn: API.user.terminateAccount,
    onSuccess: () => {
      closeModal();
      logout({ afterLogout: 'navigate-to-login' });
    },
    onError: error => {
      const { errorId } = parseApiError(error);

      switch (errorId) {
        case ErrorIds.AGREEMENT_HAS_DEBT:
          assertCondition(
            totalOutstandingDebt !== undefined,
            'Outstanding debt must be available when opening AgreementHasDebt modal.',
          );
          openModal('AGREEMENT_HAS_DEBT', {
            payload: { remainingDebt: totalOutstandingDebt },
          });
          break;
        case ErrorIds.USER_OWES_INTEREST:
        case ErrorIds.USER_OWES_PRINCIPAL_AND_INTEREST:
          openModal('REPAY_OUTSTANDING_BALANCE');
          break;
        case ErrorIds.USERS_LAST_PAYBACK_IS_STILL_PROCESSING:
          openModal('LAST_PAYBACK_STILL_PROCESSING');
          break;
        default:
          showErrorToast(t('defaultMessage'));
          closeModal();
          break;
      }
    },
  });
};

export default useTerminateAccountMutation;
